import React from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import AutoSizer, { Size } from 'react-virtualized-auto-sizer';
import isEqual from 'lodash.isequal';

// Types
import { AppState } from 'services/types';
import {
    VirtualizedTableColumnKey,
    VirtualizedTableColumnWidth,
    VirtualizedTableColumns,
    VirtualizedTableItem
} from 'services/virtualizedTable/types';
import { updateQueryString } from 'services/router/actions';

// Global Actions
import { setColumnWidth } from 'services/columnWidths/actions';

// Global Components
import VirtualizedTable from 'components/VirtualizedTable';
import { selectIsAdmin } from 'services/usersMe/selectors';

import {
    requestedCreateFertilizerProduct,
    requestedDeleteFertilizerProduct,
    requestedUpdateFertilizerProduct
} from 'services/fertilizerProducts/actions';
import {
    FertilizerProduct,
    PostFertilizerProductPayload
} from 'services/fertilizerProducts/types';
import {
    makeSelectFertilizerProductItems,
    selectProductsTableColumns
} from './selectors';

interface OwnProps {
    tableId: string;
}

interface ConnectedProps {
    items: VirtualizedTableItem[];
    columns: VirtualizedTableColumns;
    isWritable: boolean;
}

interface DispatchProps {
    setColumnWidth: typeof setColumnWidth;
    updateQueryString: typeof updateQueryString;
    createFertilizerProduct: typeof requestedCreateFertilizerProduct;
    updateFertilizerProduct: typeof requestedUpdateFertilizerProduct;
    deleteFertilizerProduct: typeof requestedDeleteFertilizerProduct;
}

type Props = OwnProps & ConnectedProps & DispatchProps;

export class FertilizerProductsVirtualizedTableComponent extends React.Component<Props> {
    shouldComponentUpdate(nextProps: Props): boolean {
        return !isEqual(this.props, nextProps);
    }

    handleColumnResized = (
        columnKey: VirtualizedTableColumnKey,
        columnWidth: VirtualizedTableColumnWidth
    ): void => {
        const { tableId, ...props } = this.props;
        props.setColumnWidth(tableId, columnKey, columnWidth);
    };

    handleCreate = (item: VirtualizedTableItem<FertilizerProduct>): void => {
        const { createFertilizerProduct } = this.props;

        createFertilizerProduct(item as PostFertilizerProductPayload);
    };

    handleDelete = (items: VirtualizedTableItem<FertilizerProduct>[]): void => {
        const { deleteFertilizerProduct } = this.props;

        if (items[0] && items[0].id) {
            deleteFertilizerProduct(items[0] as FertilizerProduct);
        }
    };

    handleItemUpdate = (
        item: VirtualizedTableItem<FertilizerProduct>
    ): void => {
        const { updateFertilizerProduct } = this.props;
        const {
            unitTypeId,
            witholdingPeriodDays,
            reEntryPeriodDays,
            nitrogenValue,
            phosphorusValue,
            potassiumValue,
            sulfurValue,
            magnesiumValue,
            calciumValue,
            id
        } = item;

        updateFertilizerProduct(id, {
            unitTypeId,
            witholdingPeriodDays,
            reEntryPeriodDays,
            nitrogenValue,
            phosphorusValue,
            potassiumValue,
            sulfurValue,
            magnesiumValue,
            calciumValue
        });
    };

    renderVirtualizedTable = ({ height, width }: Size): React.ReactElement => {
        const { tableId, items, columns, isWritable } = this.props;

        return (
            <React.Fragment>
                <VirtualizedTable
                    id={tableId}
                    height={height}
                    width={width}
                    columns={columns}
                    items={items}
                    onColumnResized={this.handleColumnResized}
                    labelPlural="Products"
                    onCreate={this.handleCreate}
                    onItemUpdate={this.handleItemUpdate}
                    onDelete={this.handleDelete}
                    enableCreating={isWritable}
                    enableItemEditing={isWritable}
                    editable={isWritable}
                    enableDeleting={isWritable}
                    selectable={isWritable}
                />
            </React.Fragment>
        );
    };

    render(): React.ReactElement {
        return (
            <AutoSizer>
                {(size: Size): React.ReactElement =>
                    this.renderVirtualizedTable(size)
                }
            </AutoSizer>
        );
    }
}

const mapStateToProps = createStructuredSelector<
    AppState,
    OwnProps,
    ConnectedProps
>({
    items: makeSelectFertilizerProductItems(),
    columns: selectProductsTableColumns,
    isWritable: selectIsAdmin
});

export default connect(mapStateToProps, {
    setColumnWidth,
    updateQueryString,
    createFertilizerProduct: requestedCreateFertilizerProduct,
    updateFertilizerProduct: requestedUpdateFertilizerProduct,
    deleteFertilizerProduct: requestedDeleteFertilizerProduct
})(FertilizerProductsVirtualizedTableComponent);
